import React from 'react';
import ExpandCollapse from '../Visualeffects/ExpandCollapse';
import SolverTwoPanelLayout from './SolverTwoPanelLayout';

export default function GaussSolverUI({ state, setState }) {
  const {
    matrixInput,
    vectorInput,
    loading,
    result,
    error,
    showInfo,
    hasMatrixError,
    hasVectorError
  } = state;

  // ---------- Formatting helpers: 3 decimals, column-aligned, right-justified ----------

  const fmt3 = (x) => {
    const n = Number(x);
    return Number.isFinite(n) ? n.toFixed(3) : String(x);
  };

  const parseToMatrix = (input) => {
    try {
      if (Array.isArray(input)) {
        return input.map(row => Array.isArray(row) ? row.map(Number) : [Number(input)]);
      }
      if (typeof input === 'string') {
        const lines = input.split(/\r?\n/);
        const rows = lines
          .map(line => {
            const nums = [...line.matchAll(/[-+]?\d*\.?\d+(?:[eE][-+]?\d+)?/g)].map(m => Number(m[0]));
            return nums.length ? nums : null;
          })
          .filter(Boolean);
        if (rows.length === 0) {
          const maybe = JSON.parse(input);
          return parseToMatrix(maybe);
        }
        return rows;
      }
    } catch { /* fallthrough */ }
    return [];
  };

  const formatAlignedMatrix = (matrixLike) => {
    const M = parseToMatrix(matrixLike);
    if (!M.length) return String(matrixLike ?? '');

    const formatted = M.map(row => row.map(v => fmt3(v)));
    const cols = Math.max(...formatted.map(r => r.length));
    const colWidths = Array.from({ length: cols }, () => 0);

    for (let r = 0; r < formatted.length; r++) {
      for (let c = 0; c < cols; c++) {
        const cell = String(formatted[r][c] ?? '');
        if (cell.length > colWidths[c]) colWidths[c] = cell.length;
      }
    }

    const lines = formatted.map(row => {
      const padded = [];
      for (let c = 0; c < cols; c++) {
        const cell = String(row[c] ?? '');
        padded.push(cell.padStart(colWidths[c], ' '));
      }
      return padded.join(' ');
    });

    return lines.join('\n');
  };

  // Convenience wrappers for the inputs on the right result panel
  const formatMatrixForDisplay = (src) => {
    try { return formatAlignedMatrix(JSON.parse(src)); }
    catch { return formatAlignedMatrix(src); }
  };

  const formatVectorForDisplay = (src) => {
    try {
      const v = JSON.parse(src);
      if (Array.isArray(v)) return formatAlignedMatrix([v]); // as single row
    } catch { /* ignore */ }
    return formatAlignedMatrix(src);
  };
  const set = (key, value) => setState(prev => ({ ...prev, [key]: value }));
  const [showAllSteps, setShowAllSteps] = React.useState(false);
  const { lastUsedMatrix = '', lastUsedVector = '' } = state;


  const VISIBLE_STEPS = 2;
  const steps = Array.isArray(result?.log) ? result.log : [];
  const head = steps.slice(0, VISIBLE_STEPS);
  const tail = steps.slice(VISIBLE_STEPS);

  const renderEntry = (entry, idx, prev) => (
    <div key={idx} className="border-b pb-4">
      <p className="font-semibold mb-2">{entry.step}</p>

      <p className="mb-1">Matrix A:</p>
      <pre className="bg-gray-100 p-2 rounded whitespace-pre-wrap">
        {entry.matrix.map((row, i) =>
          row.map((val, j) => {
            const prevVal = prev?.matrix?.[i]?.[j];
            const changed = prevVal !== undefined && Math.abs(prevVal - val) > 1e-8;
            return (
              <span key={`${i}-${j}`} className={changed ? 'text-red-600 font-bold' : ''}>
                {val.toFixed(4)}{j < row.length - 1 ? '\t' : ''}
              </span>
            );
          }).reduce((acc, val) => [...acc, val], []).concat(<br key={`br-${i}`} />)
        )}
      </pre>

      <p className="mt-2 mb-1">Vector b:</p>
      <pre className="bg-gray-100 p-2 rounded whitespace-pre-wrap">
        {entry.vector.map((val, i) => {
          const prevVal = prev?.vector?.[i];
          const changed = prevVal !== undefined && Math.abs(prevVal - val) > 1e-8;
          return (
            <span key={i} className={changed ? 'text-red-600 font-bold' : ''}>
              {val.toFixed(4)}{i < entry.vector.length - 1 ? ', ' : ''}
            </span>
          );
        })}
      </pre>
    </div>
  );


  const handleSolve = async () => {
    set('loading', true);
    set('result', null);
    set('error', null);

    try {
      const response = await fetch('https://api.numsolve.org/solve-gauss', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          matrix: JSON.parse(matrixInput),
          vector: JSON.parse(vectorInput),
        }),
      });

      const data = await response.json();
      if (data?.error) {
        set('error', data.error);
        set('result', null);
        set('hasMatrixError', true);
        set('hasVectorError', true);
        set('lastUsedMatrix', matrixInput);
        set('lastUsedVector', vectorInput);
      } else {
        set('result', data);
        set('error', null);
        set('hasMatrixError', false);
        set('hasVectorError', false);
        set('lastUsedMatrix', matrixInput);
        set('lastUsedVector', vectorInput);
        try {
          const recentEntry = {
            timestamp: new Date().toISOString(),
            method: 'gauss',
            input: {
              matrix: JSON.parse(matrixInput),
              vector: JSON.parse(vectorInput),
            },
            solution: data?.solution ?? null,
            // Prefer inline log; keep file name if backend only provided file
            log: Array.isArray(data?.log) ? data.log : null,
            log_file: data?.log_file || null,
          };
          const existing = JSON.parse(localStorage.getItem('calcLogs') || '[]');
          const updated = [recentEntry, ...existing].slice(0, 100);
          localStorage.setItem('calcLogs', JSON.stringify(updated));
          window.dispatchEvent(new Event("calcLogsUpdated"));
        } catch { }
      }
    } catch (err) {
      set('error', 'Backend not reachable.');
    }

    set('loading', false);
  };

  const inputCard = (
    <div className="rounded-3xl border border-white/30 bg-white/80 p-8 shadow-[0_30px_80px_-45px_rgba(15,20,43,0.95)] backdrop-blur">
      <div className="space-y-4">
        <div>
          <label className="mb-3 block text-sm font-bold text-gray-700">Matrix A</label>
          <div className="input-wrapper input-wrapper input-pulse-blue">
            <textarea
              value={matrixInput}
              onChange={(e) => set('matrixInput', e.target.value)}
              placeholder="e.g. [[1, 1, -1, 1], [1, -2, 1, 2], [2, -1, 2, -1], [3, 2, -1, 1]]"
              rows={4}
              className={`w-full resize-none rounded-xl border px-4 py-2 text-gray-800 ${
                hasMatrixError ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-gray-50'
              }`}
            />
          </div>
          {hasMatrixError && (
            <p className="mt-1 text-sm text-red-500">Check if matrix is valid or singular.</p>
          )}
        </div>

        <div>
          <label className="mb-3 block text-sm font-bold text-gray-700">Vector b</label>
          <div className="input-wrapper input-wrapper-tight input-pulse-blue">
            <input
              type="text"
              value={vectorInput}
              onChange={(e) => set('vectorInput', e.target.value)}
              placeholder="e.g. [2, 6, 2, 4]"
              style={{ overflow: 'visible' }}
              className={`w-full rounded-xl border px-4 py-3 text-gray-800 focus:outline-none ${
                hasVectorError ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-gray-50'
              }`}
            />
          </div>
          {hasVectorError && (
            <p className="mt-1 text-sm text-red-500">Check if vector matches matrix size.</p>
          )}
        </div>
      </div>

      <div className="mt-6">
        <button className="button button-blue" onClick={handleSolve} disabled={loading}>
          {loading ? 'Solving…' : 'Solve'}
        </button>
      </div>
    </div>
  );

  const outputCard = (
    <div
      className={`rounded-3xl border border-white/30 bg-white/80 p-8 shadow-[0_30px_80px_-45px_rgba(15,20,43,0.95)] transition-all duration-300 ${
        result && !error ? 'result-success' : ''
      }`}
    >
      <h2 className="mb-4 text-xl font-semibold text-gray-900">Results</h2>
      {result?.solution && (
        <div className="mb-6 space-y-4 text-sm text-gray-800">
          <div>
            <p className="mb-1 font-semibold">Matrix A:</p>
            <pre className="whitespace-pre overflow-x-auto rounded border border-gray-300 bg-white p-3 font-mono">
              {formatMatrixForDisplay(lastUsedMatrix || matrixInput)}
            </pre>
          </div>

          <div>
            <p className="mb-1 font-semibold">Vector b:</p>
            <pre className="whitespace-pre overflow-x-auto rounded border border-gray-300 bg-white p-3 font-mono">
              {formatVectorForDisplay(lastUsedVector || vectorInput)}
            </pre>
          </div>

          <div>
            <p className="mb-1 font-semibold">Solution vector x:</p>
            <pre className="whitespace-pre rounded border border-blue-200 bg-blue-50 p-3 font-mono text-blue-800">
              {(() => {
                const row = [result.solution.map((n) => Number(n))];
                return formatAlignedMatrix(row);
              })()}
            </pre>
          </div>
        </div>
      )}

      {result?.log && result.log.length > 0 && (
        <div className="relative mt-6">
          <h3 className="mb-2 text-md font-semibold text-gray-800">Calculation Steps</h3>

          <div className="space-y-6 font-mono text-sm text-gray-800">
            {head.map((entry, idx) => {
              const prev = idx > 0 ? head[idx - 1] : null;
              return renderEntry(entry, idx, prev);
            })}
          </div>

          {tail.length > 0 && (
            <>
              <ExpandCollapse
                open={showAllSteps}
                durationMs={800}
                ease="cubic-bezier(.25,.9,.2,1)"
                className="mt-2"
              >
                <div className="space-y-6 font-mono text-sm text-gray-800">
                  {tail.map((entry, i) => {
                    const globalIdx = VISIBLE_STEPS + i;
                    const prev = globalIdx > 0 ? steps[globalIdx - 1] : null;
                    return renderEntry(entry, `tail-${i}`, prev);
                  })}
                </div>
              </ExpandCollapse>
              <div className="mt-4 text-center">
                <button
                  onClick={() => setShowAllSteps((v) => !v)}
                  className="inline-block rounded-full border border-blue-200 bg-white px-4 py-1 text-sm font-medium text-blue-800 shadow-sm transition-all hover:bg-blue-50"
                  aria-expanded={showAllSteps}
                  aria-controls="gauss-more-steps"
                >
                  {showAllSteps ? 'Hide steps' : `Show more steps (${tail.length})`}
                </button>
              </div>
            </>
          )}
        </div>
      )}
      {!result && !error && !loading && (
        <div className="text-sm text-gray-500">No results yet. Please enter a matrix and vector.</div>
      )}
      {error && (
        <div className="mt-4 rounded-lg border border-red-300 bg-red-100 p-3 text-sm text-red-800">
          <strong>Error:</strong> {error}
        </div>
      )}
    </div>
  );

  return (
    <SolverTwoPanelLayout
      loading={loading}
      inputCard={inputCard}
      outputCard={outputCard}
    />
  );
}